// Pure lifecycle count deltas for Song Processing Active / Archived tab labels.
// Same contract as Song Library (tracks/lifecycleCountPatch.ts): optimistic
// patches must update both partitions immediately, and delayed LIST reconcile
// must be generation-guarded so a lagging projection cannot overwrite them.

export type ProcessingLifecycleCountOp = "archive" | "restore" | "delete";

export type ProcessingLifecycleCountBucket = "active" | "archived";

export interface ProcessingLifecycleCountSnapshot {
  active: number;
  archived: number;
}

/** Per-operation deltas for a countProcessingJobs cache entry. */
export function processingLifecycleCountDelta(
  op: ProcessingLifecycleCountOp,
  bucket: ProcessingLifecycleCountBucket,
): number {
  if (op === "archive") {
    if (bucket === "active") return -1;
    return 1; // archived
  }
  if (op === "restore") {
    if (bucket === "active") return 1;
    return -1;
  }
  // permanent delete (archived → gone)
  if (bucket === "archived") return -1;
  return 0;
}

export function applyProcessingLifecycleCountDelta(
  current: number,
  delta: number,
): number {
  if (typeof current !== "number" || !Number.isFinite(current) || current < 0) {
    return current;
  }
  return Math.max(0, current + delta);
}

export function applyProcessingLifecycleOpToSnapshot(
  snap: ProcessingLifecycleCountSnapshot,
  op: ProcessingLifecycleCountOp,
  times = 1,
): ProcessingLifecycleCountSnapshot {
  const n = Math.max(0, Math.floor(times));
  let active = snap.active;
  let archived = snap.archived;
  for (let i = 0; i < n; i += 1) {
    active = applyProcessingLifecycleCountDelta(
      active,
      processingLifecycleCountDelta(op, "active"),
    );
    archived = applyProcessingLifecycleCountDelta(
      archived,
      processingLifecycleCountDelta(op, "archived"),
    );
  }
  return { active, archived };
}

/**
 * Mutations must NOT immediately invalidate ProcessingJobs LIST — that
 * refetches countProcessingJobs against a lagging projection and can
 * overwrite optimistic totals (list shows 2 archived, tab still says 1).
 */
export const PROCESSING_LIFECYCLE_IMMEDIATE_LIST_INVALIDATION = false as const;

export const PROCESSING_RECONCILE_DELAY_MS = 2500;
export const PROCESSING_RECONCILE_RETRY_MS = 1500;
export const PROCESSING_RECONCILE_MAX_ATTEMPTS = 4;
/** Hard ceiling so a lagging projection cannot retry forever. */
export const PROCESSING_RECONCILE_MAX_ATTEMPTS_CEILING = 12;

/**
 * Scale reconcile retries with bulk size. Archiving 50 jobs often needs more
 * than the base 4 attempts for Active/Archived projection catch-up.
 */
export function processingReconcileMaxAttempts(
  expectedJobIdsLength = 0,
): number {
  const extra = Math.ceil(Math.max(0, expectedJobIdsLength) / 25);
  return Math.min(
    PROCESSING_RECONCILE_MAX_ATTEMPTS_CEILING,
    PROCESSING_RECONCILE_MAX_ATTEMPTS + extra,
  );
}

export type ProcessingBulkOpRecentItem = {
  jobId?: string | null;
  status?: string | null;
};

/**
 * Prefer per-item succeeded IDs from BulkOp recentItems. When the BulkOp is a
 * clean full success but recentItems is truncated/incomplete (API samples
 * ~50), fall back to the submitted jobId set so list patches + membership
 * checks cover every confirmed archive/restore.
 */
export function resolveBulkOpSucceededJobIds(args: {
  succeededCount: number;
  failedCount?: number;
  skippedCount?: number;
  totalMatched?: number;
  recentItems?: ProcessingBulkOpRecentItem[] | null;
  submittedIds?: string[] | null;
}): string[] {
  const fromRecent = Array.from(
    new Set(
      (args.recentItems || [])
        .filter(
          (item) =>
            item &&
            item.status === "succeeded" &&
            typeof item.jobId === "string" &&
            item.jobId.length > 0,
        )
        .map((item) => item.jobId as string),
    ),
  );
  const succeeded = Math.max(0, Math.floor(args.succeededCount || 0));
  const failed = Math.max(0, Math.floor(args.failedCount || 0));
  const skipped = Math.max(0, Math.floor(args.skippedCount || 0));
  const requested = Math.max(0, Math.floor(args.totalMatched || 0));

  if (
    fromRecent.length > 0 &&
    (succeeded === 0 || fromRecent.length >= succeeded)
  ) {
    return fromRecent;
  }

  const fallback = Array.from(
    new Set(
      (args.submittedIds || [])
        .map((id) => (typeof id === "string" ? id.trim() : ""))
        .filter((id) => id.length > 0),
    ),
  );

  if (
    fallback.length > 0 &&
    succeeded > 0 &&
    failed === 0 &&
    skipped === 0 &&
    (requested === 0 || succeeded === requested) &&
    (fallback.length === requested || fallback.length === succeeded)
  ) {
    return fallback;
  }

  // Partial success: keep whatever recentItems reported; never invent IDs.
  return fromRecent;
}

/**
 * After restore, Active must reach expected; after archive, Active must drop
 * and Archived must rise. Reject lagging projection responses.
 */
export function isStaleProcessingCountVersusExpected(
  bucket: ProcessingLifecycleCountBucket,
  serverCount: number,
  expected: ProcessingLifecycleCountSnapshot,
  op: ProcessingLifecycleCountOp,
): boolean {
  if (typeof serverCount !== "number" || !Number.isFinite(serverCount) || serverCount < 0) {
    return true;
  }
  if (op === "restore") {
    if (bucket === "active") return serverCount < expected.active;
    return serverCount > expected.archived;
  }
  if (op === "archive") {
    if (bucket === "active") return serverCount > expected.active;
    return serverCount < expected.archived;
  }
  // delete — archived projection still above optimistic expected is lag
  // (common after mass hard-delete), not a membership regression.
  if (bucket === "archived") return serverCount > expected.archived;
  return false;
}

/**
 * After permanent delete, destination membership means deleted jobIds must
 * be absent from both Active and Archived list pages. Lagging counts alone
 * must not be treated as a hard reconcile failure (see processingApi).
 */
export function listOmitsExpectedJobIds(
  jobs: Array<{ jobId: string }> | null | undefined,
  expectedJobIds: string[],
): boolean {
  if (!expectedJobIds.length) return true;
  if (!Array.isArray(jobs)) return false;
  const present = new Set(jobs.map((j) => j.jobId));
  return expectedJobIds.every((id) => !present.has(id));
}

/** True when a delete left the Archived tab at an intentional empty total. */
export function isEmptyArchivedDeleteTarget(
  expected: ProcessingLifecycleCountSnapshot,
): boolean {
  return expected.archived === 0;
}

/** Count-query args → Active vs Archived partition. */
export function processingCountArgsBucket(
  args: { archived?: boolean; history?: boolean } | void | null | undefined,
): ProcessingLifecycleCountBucket {
  return args && args.archived === true ? "archived" : "active";
}

/** History tab totals are independent of archive/restore patches. */
export function isHistoryCountArgs(
  args: { history?: boolean; archived?: boolean } | void | null | undefined,
): boolean {
  return !!(args && args.history === true && args.archived !== true);
}

/**
 * True when a list page includes every expected job id (membership check).
 * Used to reject lagging Archived/Active list refetches after bulk lifecycle.
 * Duplicate titles are irrelevant — identity is jobId only.
 */
export function listContainsExpectedJobIds(
  jobs: Array<{ jobId: string }> | null | undefined,
  expectedJobIds: string[],
): boolean {
  if (!expectedJobIds.length) return true;
  if (!Array.isArray(jobs)) return false;
  const present = new Set(jobs.map((j) => j.jobId));
  return expectedJobIds.every((id) => present.has(id));
}

/**
 * Regression shape: bulk-archive three jobs (two share a title) must keep all
 * three jobIds in the Archived membership set — never collapse by title.
 */
export function archivedMembershipAfterBulkArchive(
  activeJobIds: string[],
  archivedBefore: string[],
  archivedOkIds: string[],
): { active: string[]; archived: string[] } {
  const archivedSet = new Set([...archivedBefore, ...archivedOkIds]);
  const active = activeJobIds.filter((id) => !archivedOkIds.includes(id));
  return { active, archived: Array.from(archivedSet) };
}

/**
 * After restoring a subset of archived jobs, Active/Archived membership must
 * move only those jobIds — siblings with the same title stay put.
 */
export function membershipAfterBulkRestore(
  activeBefore: string[],
  archivedBefore: string[],
  restoredOkIds: string[],
): { active: string[]; archived: string[] } {
  const restoreSet = new Set(restoredOkIds);
  const archived = archivedBefore.filter((id) => !restoreSet.has(id));
  const active = Array.from(new Set([...activeBefore, ...restoredOkIds]));
  return { active, archived };
}

/**
 * After counts have caught up, destination first-page membership may still
 * lag (or exceed PAGE_SIZE). If every expected id is already gone from the
 * source partition, authoritative LIST invalidate is safe — do not toast.
 */
export function sourceClearedForLifecycleMove(
  sourceJobs: Array<{ jobId: string }> | null | undefined,
  expectedJobIds: string[],
): boolean {
  return listOmitsExpectedJobIds(sourceJobs, expectedJobIds);
}
